import React, { useEffect, useState } from "react";
import { LuFileText, LuStamp } from "react-icons/lu";
import { cn, notify } from "../../../components/ui";
import { useTemplates } from "../useTemplates";
import { t } from "../../../i18n";

// O backend devolve este texto quando ainda não há arquivo na pasta.
const NO_FILE = "Não selecionado";

interface TemplateUploadProps {
  folder: "receituarios" | "carimbos";
  title: string;
  /** Extensões aceitas, sem ponto: ["pdf"] ou ["png", "jpg", "jpeg"]. */
  extensions: string[];
  /** Formato esperado, mostrado sob o nome do arquivo. */
  hint: string;
  /** Texto do botão quando ainda não há arquivo ("Enviar PDF"). */
  uploadLabel: string;
  errorMessage: string;
  successMessage: string;
}

// Envia ou troca o receituário/carimbo: escolher o arquivo já envia.
export const TemplateUpload: React.FC<TemplateUploadProps> = ({
  folder, title, extensions, hint, uploadLabel, errorMessage, successMessage,
}) => {
  const [currentName, setCurrentName] = useState(t("settings.files.loading"));
  const { uploadTemplate, getTemplateFileName, uploading } = useTemplates();

  useEffect(() => {
    getTemplateFileName(folder).then((result) => {
      setCurrentName(result ? result.file : t("settings.files.loadError"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const selected = input.files?.[0];
    if (!selected) return;
    const ext = selected.name.split(".").pop()?.toLowerCase() ?? "";
    if (!extensions.includes(ext)) {
      notify({ tone: "error", title: errorMessage });
    } else if (await uploadTemplate(folder, selected)) {
      notify({ tone: "success", title: successMessage });
      setCurrentName(selected.name);
    }
    // Limpa para que escolher o mesmo arquivo de novo dispare o onChange.
    input.value = "";
  };

  const hasFile = ![NO_FILE, t("settings.files.loading"), t("settings.files.loadError")].includes(currentName);
  const displayName = currentName === NO_FILE ? t("settings.files.none") : currentName;
  const action = uploading ? t("settings.files.sending") : hasFile ? t("settings.files.replace") : uploadLabel;

  return (
    // O bloco inteiro abre o seletor de arquivo. Tracejado = ainda sem arquivo.
    <label
      className={cn(
        "flex min-w-0 cursor-pointer flex-col gap-3 rounded-card border-strong border-primary bg-primary-tint p-4 transition-colors duration-fast hover:bg-primary-soft",
        !hasFile && "border-dashed",
        uploading && "cursor-wait opacity-70",
      )}
    >
      {/* Faixa de cima: o que é e qual formato. */}
      <span className="flex min-w-0 items-center gap-3">
        <span className="grid h-card-icon w-card-icon shrink-0 place-items-center rounded-full bg-primary text-on-primary" aria-hidden>
          {folder === "receituarios" ? <LuFileText size={20} /> : <LuStamp size={20} />}
        </span>
        <span className="min-w-0">
          <span className="block text-body font-semibold text-fg">{title}</span>
          <span className="block text-caption text-fg-muted">{hint}</span>
        </span>
      </span>
      {/* Faixa de baixo: arquivo atual e a ação; o nome trunca, o botão não encolhe. */}
      <span className="flex min-w-0 items-center gap-3 rounded-control bg-surface py-2 pl-3 pr-2">
        <span className="min-w-0 flex-1 truncate text-caption text-fg-muted">{displayName}</span>
        <span className="shrink-0 rounded-button bg-primary px-button-sm py-1.5 text-label font-semibold text-on-primary" aria-hidden>
          {action}
        </span>
      </span>
      <input
        type="file"
        accept={extensions.map((ext) => `.${ext}`).join(",")}
        onChange={handleChange}
        disabled={uploading}
        aria-label={`${hasFile ? t("settings.files.replace") : uploadLabel}: ${title}`}
        className="sr-only"
      />
    </label>
  );
};
